import { FC } from "react";
import {
  IncreaseOfShareCapitalForm,
  PreferentialRightWaivedType,
} from "./increaseOfShareCapitalDomain";
import { observer } from "mobx-react";
import { TextQuestion, YesNoQuestion } from "@/dbot/common/commonComponents";
import { Box, VBox } from "@/components-v2/Box";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";

import { useTranslation } from "@/stores/RootStoreContext";
import { LocalizedStringFieldModel, RadioFieldModel } from "@/common/commonForms";
import { languageLabels } from "@/i18n/languages";
import { MCheckbox } from "@/components-v2/Checkbox";

const PreferentialRightQuestion = observer(({ form }: { form: IncreaseOfShareCapitalForm }) => {
  const { t } = useTranslation();
  return (
    <Box
      tooltip={
        form.shareIssue.hasIndividualSharePrice
          ? {
              key: "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRight.disabled.individualizedSharePrice",
            }
          : undefined
      }
    >
      <YesNoQuestion
        question={t("decisions.increaseShareCapitalAgainstCash.newShares.preferentialRight")}
        field={form.shareIssue.preferentialRight}
        isLocked={
          form.isProjectLocked ||
          form.currentNumberOfShares === 0 ||
          form.shareIssue.hasIndividualSharePrice
        }
      />
    </Box>
  );
});

export const PreferentialRightQuestion2 = observer(
  ({ form }: { form: IncreaseOfShareCapitalForm }) => {
    const { t } = useTranslation();
    return (
      <VBox
        tooltip={
          form.shareIssue.hasIndividualSharePrice
            ? {
                key: "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRight.disabled.individualizedSharePrice",
              }
            : undefined
        }
      >
        <div>{t("decisions.increaseShareCapitalAgainstCash.newShares.preferentialRight2")}</div>
        <MCheckbox
          model={form.shareIssue.preferentialRight}
          disabled={
            form.isProjectLocked ||
            form.currentNumberOfShares === 0 ||
            form.shareIssue.hasIndividualSharePrice ||
            !form.containsCash
          }
          label={t(
            "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRight.enabledText",
          )}
        />
      </VBox>
    );
  },
);

const ShowPreferentialRight: FC<{ form: IncreaseOfShareCapitalForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <>
      <PreferentialRightQuestion form={form} />
      {form.isPreferentialRight && (
        <>
          <YesNoQuestion
            question={t(
              "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRightTransferable",
            )}
            field={form.shareIssue.preferentialRightTransferable}
            isLocked={form.isProjectLocked}
          />
          <YesNoQuestion
            question={t(
              "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRightOversubscribe",
            )}
            field={form.shareIssue.preferentialRightOversubscribe}
            isLocked={form.isProjectLocked}
          />
          <YesNoQuestion
            question={t(
              "decisions.increaseShareCapitalAgainstCash.newShares.preferentialBoardRightsToSellSubscription",
            )}
            field={form.shareIssue.preferentialBoardRightsToSellSubscription}
            isLocked={form.isProjectLocked}
          />
          {
            // Not making any sense having external subscribers since not having allocation functionality.
            // Should add, and support, this once allocation is available.
            /*
          <YesNoQuestion
            question={t(
              "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRightExternalPeople",
            )}
            field={form.shareIssue.externalSubscribers}
            onChange={(e) => form.shareIssue.setExternalSubscribers(e)}
            isLocked={form.isProjectLocked}
          />*/
          }
        </>
      )}
      {form.showPreferentialRightWaivedReason && (
        <PreferentialRightWaivedReason
          model={form.shareIssue.preferentialRightWaivedReasonModel}
          preferentialRightWaivedReasonTexts={form.shareIssue.preferentialRightWaivedReasonTexts}
          isProjectLocked={form.isProjectLocked}
        />
      )}
    </>
  );
});

const PreferentialRightWaivedReason = observer(function ({
  model,
  preferentialRightWaivedReasonTexts,
  isProjectLocked,
}: {
  model: RadioFieldModel<PreferentialRightWaivedType | undefined>;
  preferentialRightWaivedReasonTexts: LocalizedStringFieldModel[];
  isProjectLocked: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <VBox>
        <div>
          {t(
            "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRightDeviationReason.questionText",
          )}
        </div>
        <MRadioButtonGroup<PreferentialRightWaivedType | undefined>
          name={"preferentialRightWaivedReason"}
          model={model}
          onChange={(_, value) => model.setValue(value)}
          disabled={isProjectLocked}
        />
      </VBox>
      {model.value === PreferentialRightWaivedType.ADDED_EXPLANATION && (
        <VBox>
          {preferentialRightWaivedReasonTexts.map((text) => (
            <TextQuestion
              key={text.locale}
              question={
                t(
                  "decisions.increaseShareCapitalAgainstCash.newShares.preferentialRightDeviationReasonDescription",
                ) +
                (preferentialRightWaivedReasonTexts.length > 1
                  ? " (" + t(languageLabels[text.locale]) + ")"
                  : "")
              }
              field={text.model}
              isLocked={isProjectLocked}
            />
          ))}
        </VBox>
      )}
    </>
  );
});

export default ShowPreferentialRight;
